/**
 * Constants for the Account Leadsheet 294 feature.
 */
import {currentResource} from '../../../../../../../../util/utils';

/** Shared frozen empty Set to avoid re-creating default state on every render. */
export const EMPTY_SET = Object.freeze(new Set());

/** Financial Statement Type IDs grouped by section (API schema) */
export const FS_TYPE_IDS = {
	BALANCE_SHEET: [1, 4, 7], // Asset, Liability, Equity
	INCOME_STATEMENT: [8, 9, 17] // Revenue, Expense, NetIncomeExpense
};

/** GL Account Type IDs (API schema) - used for waived detection and Inverted T */
export const GL_ACCOUNT_TYPES = {
	REGULAR: 1,
	REMOVE: 0,
	IMMATERIAL: 12,
	WAIVED: 13
};

/** Hierarchy level identifiers */
export const HIERARCHY_LEVELS = {
	L1: 'financialStatement',
	L2: 'mainCategory',
	L3: 'subCategory',
	L4: 'accountGroup',
	GL_ITEM: 'glItem'
};

/** Row type discriminators used in the flattened row array for the treegrid. */
export const ROW_TYPES = {
	BREADCRUMB: 'breadcrumb', // L3 section header
	SUB_CLASS: 'subClass', // AccountSubClass accordion row
	GL: 'gl' // GL account leaf row
};

/** Pixel heights per row type, consumed by LazyTable for virtualisation. */
export const ROW_HEIGHTS = {
	[ROW_TYPES.BREADCRUMB]: 32,
	[ROW_TYPES.SUB_CLASS]: 32,
	[ROW_TYPES.GL]: 32
};

/** Testing status values */
export const TESTING_STATUS = {
	TESTED: 'tested',
	UNTESTED: 'untested',
	TESTED_BALANCES: 'tested_balances',
	UNTESTED_BALANCES: 'untested_balances',
	INCOMPLETE: 'incomplete'
};

/** Testing status filter keys used by filter config and callbacks. */
export const TESTING_STATUS_FILTER_KEYS = {
	CONFIG: 'TestingStatus',
	NAME: 'TestingStatus'
};

/** API filter tokens for GL account type query params. */
export const GL_ACCOUNT_TYPE_FILTERS = {
	IMMATERIAL: 'IMMATERIAL'
};

/** Change direction indicators returned by formatPercentChange */
export const CHANGE_DIRECTION = {
	UP: 'up',
	DOWN: 'down'
};

/** Comparative period type identifiers */
export const COMPARATIVE_PERIOD_TYPES = {
	PRIMARY: 'comparativePeriod',
	SECONDARY: 'secondComparativePeriod'
};

/** Pagination target identifiers - which leadsheet section renders the LazyTable pagination footer. */
export const PAGINATION_TARGETS = {
	BALANCE_SHEET: 'balance',
	INCOME_STATEMENT: 'income'
};

/**
 * Aggregate selection state for a section / select-all checkbox.
 * Drives `checked` and `indeterminate` rendering on Motif Checkbox.
 */
export const SECTION_SELECTION_STATE = Object.freeze({
	NONE: 'none',
	SOME: 'some',
	ALL: 'all'
});

/** Keys used with commonStore (maintainState) to persist UI state across re-renders. */
export const LEADSHEET_STATE_KEYS = {
	BS_COLLAPSED_L4_IDS: 'leadsheet294_bs_collapsedL4Ids',
	IS_COLLAPSED_L4_IDS: 'leadsheet294_is_collapsedL4Ids',
	BS_SELECTED_GL_IDS: 'leadsheet294_bs_selectedGLIds',
	IS_SELECTED_GL_IDS: 'leadsheet294_is_selectedGLIds',
	ANNOTATION_MODAL: 'leadsheet294_annotationModal',
	ANNOTATION_MODAL_FORM: 'leadsheet294_annotationModalForm',
	ANNOTATIONS_LIST_MODAL: 'leadsheet294_annotationsListModal',
	RELATE_REFERENCES_MODAL: 'leadsheet294_relateReferencesModal',
	BULK_RELATE_REFERENCES_MODAL: 'leadsheet294_bulkRelateReferencesModal',
	RELATE_ANNOTATIONS_MODAL: 'leadsheet294_relateAnnotationsModal',
	BULK_RELATE_ANNOTATIONS_MODAL: 'leadsheet294_bulkRelateAnnotationsModal',
	UPLOAD_REFERENCES_REQUEST: 'leadsheet294_uploadReferencesRequest',
	UPLOADING_REFERENCES_IN_PROGRESS: 'leadsheet294_uploadingReferencesInProgress',
	REMOVE_REFERENCES_CONFIRMATION_MODAL: 'leadsheet294_removeReferencesConfirmationModal',
	REMOVE_ANNOTATIONS_CONFIRMATION_MODAL: 'leadsheet294_removeAnnotationsConfirmationModal',
	REMOVING_REFERENCES_IN_PROGRESS: 'leadsheet294_removingReferencesInProgress',
	IS_REMOVING_ANNOTATIONS: 'leadsheet294_isRemovingAnnotations',
	IS_REMOVING_ANNOTATIONS_MODAL: 'leadsheet294_isRemovingAnnotationsModal',
	BULK_MODE_ACTIVE: 'leadsheet294_bulkModeActive',
	SELECT_ALL_ACTIVE: 'leadsheet294_selectAllActive',
	SEARCH_QUERY: 'leadsheet294_searchQuery',
	COMPARATIVE_PERIOD_SELECTION: 'leadsheet294_comparativePeriodId',
	VIEW_MODE: 'leadsheet294_viewMode',
	VISIBLE_COLUMNS: 'leadsheet294_visibleColumns',
	CONFIRM_IMMATERIAL_MODAL: 'leadsheet294_confirmImmaterialModal',
	DESIGNATE_IMMATERIAL_MODAL: 'leadsheet294_designateImmaterialModal',
	IMMATERIAL_OP_IN_PROGRESS: 'leadsheet294_immaterialOpInProgress',
	TESTING_FILTER_LOADING: 'leadsheet294_testingFilterLoading',
	ACTIVE_REFERENCE_TOOLTIP_ID: 'leadsheet294_activeReferenceTooltipId',
	REFERENCE_TOOLTIP_REFRESH_NONCE: 'leadsheet294_referenceTooltipRefreshNonce'
};

export const COLUMN_OPTIONS = {
	COMPARATIVE_DATE: 'comparativeDate',
	PERCENT_CHANGE: 'percentChange'
};

export const DEFAULT_VISIBLE_COLUMNS = new Set([COLUMN_OPTIONS.COMPARATIVE_DATE, COLUMN_OPTIONS.PERCENT_CHANGE]);

/** View mode options for the leadsheet view switcher */
export const VIEW_MODES = {
	FINANCIAL_STATEMENT: 'financialStatement',
	REFERENCE: 'reference'
};

export const FILTER_HAS_RELATED_REFERENCE = {
	NO_RELATED_EVIDENCE_AND_GL_ACCOUNT_TYPE_ID_IS_NULL: 'noRelatedEvidenceAndGlAccountTypeIdIsNull',
	NO_RELATED_EVIDENCE_AND_GL_ACCOUNT_TYPE_ID_IS_NOT_NULL: 'noRelatedEvidenceAndGlAccountTypeIdIsNotNull',
	HAS_RELATED_EVIDENCE: 'hasRelatedEvidence'
};

/**
 * API `viewType` values for the GetByIdV2 trial balance endpoint.
 * Mirrors Hercules `LeadsheetViewTypeEnum` (None = 0, AccountSubClass = 1, Document = 2).
 * The Document view groups GL accounts by their related document.
 */
export const LEADSHEET_VIEW_TYPES = {
	None: 0,
	FINANCIAL_STATEMENT: 1,
	DOCUMENT: 2
};

/**
 * Document group discriminators returned by the Document view (`viewType:1`).
 * Matches Hercules `DocumentGroupTypeEnum.ToString()` group names.
 */
export const DOCUMENT_GROUP_NAMES = Object.freeze({
	TESTED: 'Tested',
	UNTESTED: 'Untested',
	INCOMPLETE: 'Incomplete'
});

/**
 * Maps a Document view group name (Tested/Untested/Incomplete) to its localized label.
 * Falls back to the raw group name when no localized label exists.
 * @param {string} groupByName - Group name from the API (`AccountLSFSModel.groupByName`).
 * @returns {string}
 */
export const getDocumentGroupLabel = (groupByName) => {
	const leadsheetLabels = currentResource.leadsheetLabels;
	switch (groupByName) {
		case DOCUMENT_GROUP_NAMES.TESTED:
			return leadsheetLabels.tested;
		case DOCUMENT_GROUP_NAMES.UNTESTED:
			return leadsheetLabels.untested;
		case DOCUMENT_GROUP_NAMES.INCOMPLETE:
			return leadsheetLabels.incomplete;
		default:
			return groupByName || '';
	}
};

/**
 * Maps a Document view group name to the label of its single Level 2 row for the groups
 * that have no document: Untested renders an "Immaterial" row and Incomplete renders an
 * "Incomplete" row (both localized from the resource file, per the story acceptance criteria).
 * Returns an empty string for groups whose Level 2 rows use real document names (Tested).
 * @param {string} groupByName - Group name from the API (`AccountLSFSModel.groupByName`).
 * @returns {string}
 */
export const getReferenceGroupRowLabel = (groupByName) => {
	const leadsheetLabels = currentResource.leadsheetLabels;
	switch (groupByName) {
		case DOCUMENT_GROUP_NAMES.UNTESTED:
			return leadsheetLabels.immaterial;
		case DOCUMENT_GROUP_NAMES.INCOMPLETE:
			return leadsheetLabels.incomplete;
		default:
			return '';
	}
};

/**
 * Builds a documentId-scoped generic-state key for the current pagination page.
 * Scoping by documentId isolates the page state across different leadsheet documents.
 * Note: `AccountLeadsheetContainer294` clears this key on unmount, so the page resets
 * to 1 on next mount by design — the scope exists only for the lifetime of a mounted
 * container, not across remounts.
 * @param {string | number} documentId
 * @returns {string}
 */
export const getCurrentPageStateKey = (documentId) => `leadsheet294_currentPage_${documentId}`;

/** Stable, non-localized prefix for the leadsheet customFilters Redux key. */
export const LEADSHEET_FILTER_KEY_PREFIX = 'leadsheet294-filter';

/** Builds a documentId-scoped customFilters key for the leadsheet toolbar filters. */
export const getLeadsheetFilterKey = (documentId) => `${LEADSHEET_FILTER_KEY_PREFIX}-${documentId}`;

/**
 * Minimum GL account count above which the pagination control is shown.
 * Matches the V2 page size (500) so a single-page response hides pagination.
 */
export const LEADSHEET_PAGINATION_THRESHOLD = 500;

/** Fixed page size for the V2 Account Leadsheet paginated response. */
export const LEADSHEET_PAGE_SIZE = 500;

/** Trial Balance API version used by the V2 Account Leadsheet endpoint. */
export const TRIAL_BALANCE_API_VERSION = '2';

/** Single-option page-size list (per spec: pagination dropdown is locked to 500). */
export const LEADSHEET_PAGE_SIZE_OPTIONS = [LEADSHEET_PAGE_SIZE];

/** Lazy loading configuration for LazyTable */
export const LAZY_LOAD_CONFIG = {
	rootMargin: '1000px 0px',
	scrollMargin: '1000px 0px',
	threshold: 0,
	initialVisibleCount: 40,
	scrollContainerSelector: '.BodyContainerWrapper',
	enableBatchProcessing: true
};

export const FLUSH_CONFIG = {
	idleDelayMs: 40,
	stuckTimeoutMs: 200,
	batchSize: 18,
	batchDelayMs: 20
};

/**
 * Connector type identifiers used in the leadsheet to differentiate between annotation and reference connectors.
 */
export const CONNECTOR_TYPES = {
	ANNOTATION: 'annotation',
	REFERENCE: 'reference',
	WAIVED: 'waived'
};

/**
 * Returns localised context menu options for GL account rows.
 * @returns {Array<{key: string, label: string, actions: Array<{key: string, label: string}>}>}
 */
export const getContextMenuOptions = () => {
	const leadsheetLabels = currentResource.leadsheetContextMenuLabels;
	return [
		{
			key: 'reference',
			label: leadsheetLabels.references,
			actions: [
				{
					key: CONTEXT_ACTION_KEYS.RELATE_FILES,
					label: leadsheetLabels.relateFiles
				},
				{
					key: CONTEXT_ACTION_KEYS.UPLOAD_FILES,
					label: leadsheetLabels.uploadFiles
				},
				{
					key: CONTEXT_ACTION_KEYS.REMOVE_FILE,
					label: leadsheetLabels.removeFile
				}
			]
		},
		{
			key: 'waive',
			label: leadsheetLabels.immaterial,
			actions: [
				{
					key: CONTEXT_ACTION_KEYS.DESIGNATE_IMMATERIAL,
					label: leadsheetLabels.designateImmaterial
				},
				{
					key: CONTEXT_ACTION_KEYS.REMOVE_IMMATERIAL,
					label: leadsheetLabels.removeImmaterial
				}
			]
		},
		{
			key: 'annotations',
			label: leadsheetLabels.tickmarks,
			actions: [
				{
					key: CONTEXT_ACTION_KEYS.RELATE_ANNOTATIONS,
					label: leadsheetLabels.relateTickmarks
				},
				{
					key: CONTEXT_ACTION_KEYS.NEW_ANNOTATION,
					label: leadsheetLabels.newTickmark
				},
				{
					key: CONTEXT_ACTION_KEYS.REMOVE_ANNOTATIONS,
					label: leadsheetLabels.removeTickmarks
				}
			]
		}
	];
};

/**
 * Canonical action keys used by GL account context menu item disable/action logic.
 */
export const CONTEXT_ACTION_KEYS = {
	// Action key for relating files/references.
	RELATE_FILES: 'relateFiles',
	// Action key for uploading files.
	UPLOAD_FILES: 'uploadFiles',
	// Action key for creating new annotation.
	NEW_ANNOTATION: 'newAnnotation',
	// Action key for relating annotations.
	RELATE_ANNOTATIONS: 'relateTickmarks',
	// Action key for adding waived state.
	DESIGNATE_IMMATERIAL: 'designateImmaterial',
	// Action key for removing related file references.
	REMOVE_FILE: 'removeFile',
	// Action key for removing waived state.
	REMOVE_IMMATERIAL: 'removeImmaterial',
	// Action key for removing related annotations.
	REMOVE_ANNOTATIONS: 'removeAnnotations'
};

/**
 * Returns localized trigger label for GL context menu.
 * @returns {string}
 */
export const getContextMenuTriggerLabel = () => {
	return currentResource.leadsheetLabels.addTickmarksOrReferences;
};

/** Menu modes for the shared GLAccountContextMenu component. */
export const CONTEXT_MENU_MODES = Object.freeze({
	ROW: 'row',
	BULK: 'bulk'
});

/** Stable element ids used by the leadsheet menus. The GL row context menu suffixes its id with the account id. */
export const DROPDOWN_IDS = Object.freeze({
	BULK_ACTION: 'leadsheet-bulkactions-dropdown',
	GL_CONTEXT_MENU_PREFIX: 'gl-context-menu'
});

/** Modes for the unified RemoveReferencesConfirmationModal. */
export const REMOVE_REFERENCES_MODES = Object.freeze({
	SINGLE: 'single',
	BULK: 'bulk'
});

/** Which GL-account bucket a refresh targets (documents vs tags/tickmarks). */
export const GL_REFRESH_TYPES = Object.freeze({DOCUMENTS: 'documents', TAGS: 'tags'});
